import { useCallback, useMemo, useState } from 'react';

import { generateUtilityClasses, List, styled } from '@mui/material';
import { np, useTranslation } from '@kc-console/core';

import { Divider } from '../Divider';
import { MenuOptionItem } from '../MenuOptionItem';
import { MenuSectionItem } from '../MenuSectionItem';
import { filterAndSortMenuListItems } from './menuListSearch';
import { getMenuListOptionId } from './utils';

import type { MouseEvent } from 'react';
import type {
  MenuListColor,
  MenuListItemData,
  MenuListOptionItemData,
  MenuListProps,
  MenuListSectionItemData,
} from './types';

// ============================================================================
// Classes
// ============================================================================

export const menuListClasses = generateUtilityClasses('MenuList', [
  'root',
  'divider',
  'sectionHeader',
]);

// ============================================================================
// Main Component
// ============================================================================

/**
 * MenuList 컴포넌트
 *
 * DropdownMenu2, SelectableList, TransferList 등에서 공유되는 메뉴 리스트 컴포넌트입니다.
 * 가상 포커스 네비게이션, 섹션 접기/펴기, 검색어 하이라이트 등을 지원합니다.
 *
 * @example
 * ```tsx
 * // 기본 사용
 * <MenuList
 *   items={items}
 *   isItemFocused={(key) => focusedItemKey === key}
 *   onItemClick={(e, key) => handleClick(key)}
 * />
 *
 * // useMenuListNavigation과 함께 사용
 * const navigation = useMenuListNavigation({ items, ... });
 * <MenuList
 *   ref={navigation.listRef}
 *   items={items}
 *   isItemFocused={navigation.isItemFocused}
 *   onKeyDown={navigation.handleKeyDown}
 * />
 * ```
 */
export default function MenuList(props: MenuListProps) {
  const { t } = useTranslation();
  const {
    autoFocusItem = false,
    className,
    color = 'default',
    disableTooltipOnEllipsis = false,
    ellipsisTooltipPlacement = 'right',
    enableHighlight = true,
    expandable,
    filterItems: filterItemsProp,
    getItemProps,
    id,
    isItemFocused,
    items,
    noFilteredOptionsText = t('common:common.list.no_matching_items'),
    noOptionsText = t('common:common.list.no_selectable_items'),
    onBlur,
    onFocus,
    onItemClick,
    onKeyDown,
    onToggleSectionFolded,
    renderContent,
    role,
    sectionFolded: externalSectionFolded,
    selectedType = 'check',
    tabIndex = 0,
    searchText = '',
    ...others
  } = props;
  const hasSemanticListbox = role === 'listbox' && !!id;

  // ========== Section 접기/펴기 상태 (Internal) ==========
  const [internalSectionFolded, setInternalSectionFolded] = useState(new Set<string>());
  const sectionFolded = externalSectionFolded ?? internalSectionFolded;

  // ========== Items 필터링·정렬 (searchText 기반, @kc-console/core 검색 우선순위) ==========
  const filterItems = useCallback(
    (items: readonly MenuListItemData[]) => {
      if (filterItemsProp) {
        return filterItemsProp(items, searchText);
      }
      return filterAndSortMenuListItems(items, searchText);
    },
    [filterItemsProp, searchText],
  );

  const filteredItems = useMemo(
    () => (searchText ? filterItems(items) : items),
    [items, searchText, filterItems],
  );

  const handleToggleSectionFolded = useCallback(
    (key: string) => {
      if (onToggleSectionFolded) {
        onToggleSectionFolded(key);
      } else {
        setInternalSectionFolded(prev => {
          const next = new Set(prev);
          if (next.has(key)) {
            next.delete(key);
          } else {
            next.add(key);
          }
          return next;
        });
      }
    },
    [onToggleSectionFolded],
  );

  // ========== Option Item 렌더링 ==========
  const renderOptionItem = useCallback(
    (item: MenuListOptionItemData, itemKey: string) => {
      const {
        'aria-label': ariaLabel,
        className: itemClassName,
        disabled,
        disableTooltipOnEllipsis: itemDisableTooltipOnEllipsis,
        endBadge,
        icon,
        interactive = true,
        onClick,
        renderItem,
        selected,
        selectedType: itemSelectedType = selectedType,
        showDividerAbove = false,
        startBadge,
        subTexts,
        truncateSubTextIndex,
        suffix,
        text,
        tooltip,
      } = item;
      const resolvedDisableTooltipOnEllipsis =
        itemDisableTooltipOnEllipsis ?? disableTooltipOnEllipsis;

      const isFocused = isItemFocused?.(itemKey) ?? false;
      const navigationItemProps = getItemProps?.(itemKey);
      const semanticOptionProps = hasSemanticListbox
        ? {
            'aria-selected': !!selected,
            id: getMenuListOptionId(id, itemKey),
            role: 'option' as const,
          }
        : undefined;

      const handleItemClick = (event: MouseEvent<HTMLLIElement>) => {
        if (disabled || !interactive) return;
        onClick?.(event);
        onItemClick?.(event, itemKey);
      };

      // renderItem이 정의된 경우 커스텀 렌더링
      if (renderItem) {
        return [
          showDividerAbove && <MenuListDivider key={`divider-above-${itemKey}`} color={color} />,
          renderItem({
            'aria-label': ariaLabel,
            className: itemClassName,
            color,
            disabled,
            disableTooltipOnEllipsis: resolvedDisableTooltipOnEllipsis,
            ellipsisTooltipPlacement,
            endBadge,
            focused: isFocused,
            icon,
            interactive,
            key: itemKey,
            onClick: handleItemClick,
            selected,
            selectedType: itemSelectedType,
            startBadge,
            subTexts,
            truncateSubTextIndex,
            suffix,
            text,
            tooltip,
            'data-menu-item-key': itemKey,
            ...navigationItemProps,
            ...semanticOptionProps,
          }),
        ];
      }

      return [
        showDividerAbove && <MenuListDivider key={`divider-above-${itemKey}`} color={color} />,
        <MenuOptionItem
          key={itemKey}
          aria-label={ariaLabel}
          className={itemClassName}
          color={color}
          data-menu-focus-item
          data-menu-item-key={itemKey}
          disabled={disabled}
          disableTooltipOnEllipsis={resolvedDisableTooltipOnEllipsis}
          ellipsisTooltipPlacement={ellipsisTooltipPlacement}
          enableHighlight={enableHighlight}
          endBadge={endBadge}
          focused={isFocused}
          highlightText={searchText}
          icon={icon}
          interactive={interactive}
          onClick={handleItemClick}
          renderContent={renderContent}
          selected={selected}
          selectedType={itemSelectedType === 'check' ? 'check' : itemSelectedType}
          startBadge={startBadge}
          subTexts={subTexts}
          truncateSubTextIndex={truncateSubTextIndex}
          suffix={suffix}
          tabIndex={-1}
          text={text}
          tooltip={tooltip}
          {...navigationItemProps}
          {...semanticOptionProps}
        />,
      ];
    },
    [
      color,
      disableTooltipOnEllipsis,
      ellipsisTooltipPlacement,
      enableHighlight,
      searchText,
      isItemFocused,
      onItemClick,
      selectedType,
      getItemProps,
      hasSemanticListbox,
      id,
      renderContent,
    ],
  );

  // ========== Section Item 렌더링 ==========
  const renderSectionItem = useCallback(
    (item: MenuListSectionItemData, itemIndex: string) => {
      const {
        className: sectionClassName,
        expandable: itemExpandable = expandable,
        header,
        subheader,
        items: sectionItems,
        showDividerAbove = itemIndex !== '0',
        title,
      } = item;

      const isExpanded = !sectionFolded.has(itemIndex);
      const sectionHeaderKey = `section-${itemIndex}`;
      const isFocused = isItemFocused?.(sectionHeaderKey) ?? false;

      return [
        showDividerAbove && <MenuListDivider key={`divider-above-${itemIndex}`} color={color} />,
        <MenuSectionItem
          key={itemIndex}
          className={sectionClassName}
          color={color}
          expandable={itemExpandable}
          expanded={isExpanded}
          focusedHeader={isFocused}
          onExpandedChange={() => handleToggleSectionFolded(itemIndex)}
          slotProps={{
            header: itemExpandable
              ? {
                  'data-menu-focus-item': true,
                  'data-menu-item-key': sectionHeaderKey,
                  tabIndex: -1,
                  ...getItemProps?.(sectionHeaderKey),
                }
              : undefined,
          }}
          title={title}
          subheader={subheader ?? header}
        >
          {(sectionItems ?? []).map((child, childIndex) =>
            renderOptionItem(child, `${itemIndex}-${childIndex}`),
          )}
        </MenuSectionItem>,
      ];
    },
    [
      color,
      expandable,
      getItemProps,
      handleToggleSectionFolded,
      isItemFocused,
      renderOptionItem,
      sectionFolded,
    ],
  );

  // ========== Items 렌더링 ==========
  const renderedItems = useMemo(() => {
    if (filteredItems.length === 0) {
      const emptyText = searchText ? noFilteredOptionsText : noOptionsText;
      if (typeof emptyText === 'string') {
        return (
          <MenuOptionItem
            aria-selected={hasSemanticListbox ? false : undefined}
            color={color}
            disabled
            disableTooltipOnEllipsis={disableTooltipOnEllipsis}
            ellipsisTooltipPlacement={ellipsisTooltipPlacement}
            interactive={false}
            role={hasSemanticListbox ? 'option' : undefined}
            text={emptyText}
          />
        );
      }
      return emptyText;
    }

    return filteredItems.map((item: MenuListItemData, index) => {
      if (item.type === 'section') {
        return renderSectionItem(item, `${index}`);
      }
      return renderOptionItem(item, `${index}`);
    });
  }, [
    filteredItems,
    color,
    disableTooltipOnEllipsis,
    ellipsisTooltipPlacement,
    hasSemanticListbox,
    noFilteredOptionsText,
    noOptionsText,
    renderOptionItem,
    renderSectionItem,
    searchText,
  ]);

  return (
    <MenuListRoot
      autoFocus={autoFocusItem}
      className={className}
      id={id}
      role={role ?? 'listbox'}
      tabIndex={tabIndex}
      onBlur={onBlur}
      onFocus={onFocus}
      onKeyDown={onKeyDown}
      {...others}
    >
      {renderedItems}
    </MenuListRoot>
  );
}

// ============================================================================
// Styled Components
// ============================================================================

const MenuListRoot = styled(List, {
  target: menuListClasses.root,
})`
  overflow-y: auto;
  outline: none;
  padding: 0px 0px;
  scrollbar-color: rgba(0, 0, 0, 0.2) transparent;
`;

const MenuListDivider = styled(Divider, {
  target: menuListClasses.divider,
  shouldForwardProp: np('color'),
})<{
  color: MenuListColor;
}>`
  .${menuListClasses.root} & {
    margin: 8px 16px;
    border-color: ${({ theme, color }) =>
      color !== 'global'
        ? theme.color['border/weaker/default']
        : theme.color['border/global/default']};
  }
`;
